import React from 'react'
import { Chart } from 'react-chartjs-2'

import { useDatasetStyleProps, IChartJsDatasetStyles } from 'src/commons/chartjs'

import { GranularityEnum, IGenericBucket } from '../types'
import { getUniqueCategories } from '../utils'

import {
  buildHistogramOptions,
  getChartPlugins,
  getLabelsWithGranularity,
  IGraphProps,
  STYLE_CHART_CONTAINER,
} from './graphCommons'
import { useGraphContext } from './GraphWrapper/GraphWrappers'
import { DEFAULT_COLORS } from './themes'

const getDocCount = (bucket: IGenericBucket): number => bucket.doc_count || 0

function getDatasets(
  buckets: IGenericBucket[],
  datasetStylesMapping: Record<string, IChartJsDatasetStyles>,
  hiddenLabels?: string[],
  formatLabel: (x: any) => string = (x: any) => x,
  orderBy?: (a: any, b: any) => number
) {
  const stackedBuckets = buckets.map((b: any): any => b.stackedBy && b.stackedBy.buckets).filter(Boolean)
  const uniqueCategories = getUniqueCategories(stackedBuckets)

  let datasets: any[] = uniqueCategories.map((category: string) => {
    const datasetStyles = datasetStylesMapping[category]
    return {
      ...datasetStyles,
      label: formatLabel(category),
      data: stackedBuckets.map((bucket: any[]): number =>
        getDocCount(bucket.find((b: IGenericBucket) => b.key === category) || {})
      ),
    }
  })
  if (orderBy) {
    datasets.sort(orderBy)
  }

  if (hiddenLabels) {
    datasets = datasets.map((dataset: any) => ({
      ...dataset,
      hidden: hiddenLabels.includes(dataset.baseLabel),
    }))
  }

  return datasets
}

interface IProps extends IGraphProps {
  adjacentBars?: boolean
  enableAnimation?: boolean
  formatLabel?: (x: any) => string
  granularity: GranularityEnum
  orderBy?: (a: any, b: any) => number
  rawData: IGenericBucket[]
}

function TemporalStackedHistogram({
  title = '',
  showTitle,
  w = 600,
  h = 300,
  rawData,
  formatWithUnit,
  granularity,
  adjacentBars,
  hiddenLabels,
  maxValue = 0,
  enableAnimation = true,
  xTicksFormatter,
  datasetCategory,
  formatLabel,
  orderBy,
}: IProps) {
  const { reactChart } = useGraphContext()

  const labels: string | string[] = getLabelsWithGranularity(rawData, granularity)

  const datasetStylesMapping = useDatasetStyleProps(datasetCategory, DEFAULT_COLORS)
  const datasets = getDatasets(rawData, datasetStylesMapping, hiddenLabels, formatLabel, orderBy)
  // We show the legend if there are more than 1 datasets to show on the graph
  const displayLegend = datasets.length > 1
  const options = buildHistogramOptions({
    title,
    showTitle,
    maxValue,
    enableAnimation,
    formatWithUnit,
    adjacentBars,
    displayLegend,
    xTicksFormatter,
    horizontal: false,
  })

  const plugins = getChartPlugins()

  return (
    <div style={STYLE_CHART_CONTAINER}>
      <Chart
        title={title}
        ref={reactChart.chartRef}
        type="bar"
        data={{ labels, datasets }}
        width={w}
        height={h}
        options={options}
        plugins={plugins}
      />
    </div>
  )
}

export default TemporalStackedHistogram
